import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { getTranslation } from 'helpers/getTranslation';
import { idxMap } from 'helpers/styleMapIdx';

import { Typography } from 'components/shared/Typography';

import { Tooltip } from 'components/Tooltip';

import {
  EXIT_CAUSES,
  getExitCauseLabel,
  getExitScreenKey,
  getScreenLabel,
  type ExitScreenStat,
} from '../../TimelineSection.helpers';
import { getExitScreenProblem } from '../../journeySessionStory';

import styles from './ExitScreens.module.scss';

const PROBLEM_TEXT_VARIANTS = {
  error: styles.problemError,
  stuck: styles.problemStuck,
};

const CAUSE_COLOR_VARIANTS = {
  error: styles.causeError,
  stuck: styles.causeStuck,
  idle: styles.causeIdle,
  left: styles.causeLeft,
  goal: styles.causeGoal,
};

/** "Where this student leaves": last screens of sessions, stacked by exit cause. */
const ExitScreens = ({
  totalSessions,
  exitScreenStats,
}: {
  totalSessions: number;
  exitScreenStats: ExitScreenStat[];
}) => {
  useTranslation();
  const maxTotal = Math.max(...exitScreenStats.map((stat) => stat.total), 1);

  return (
    <section className={styles.panel}>
      <div className={styles.header}>
        <Typography variant="body1" weight="semiBold" mode="compact">
          {getTranslation('student_page.journey.exit_screens.title')}
        </Typography>
        <Typography className={styles.hint} variant="body3" mode="compact">
          {getTranslation('student_page.journey.exit_screens.hint')}
        </Typography>
      </div>

      <div className={styles.legend}>
        {EXIT_CAUSES.map((cause) => (
          <span key={cause} className={styles.legendItem}>
            <span
              className={cn(
                styles.legendSwatch,
                idxMap(CAUSE_COLOR_VARIANTS as Record<string, string>, cause),
              )}
            />
            <Typography
              className={styles.legendText}
              component="span"
              variant="body3"
              mode="compact"
            >
              {getExitCauseLabel(cause)}
            </Typography>
          </span>
        ))}
      </div>

      {exitScreenStats.length === 0 || totalSessions === 0 ? (
        <Typography className={styles.empty} variant="body2">
          {getTranslation('student_page.journey.exit_screens.empty')}
        </Typography>
      ) : (
        <div className={styles.rows}>
          {exitScreenStats.map((stat) => {
            const label = getScreenLabel(stat.eventName, stat.properties);
            const problem = getExitScreenProblem(stat);

            return (
              <div key={getExitScreenKey(stat)} className={styles.row}>
                <Tooltip
                  text={label}
                  triggerClassName={styles.nameTrigger}
                  triggerElement={
                    <Typography
                      className={styles.name}
                      variant="body3"
                      weight="medium"
                      mode="compact"
                    >
                      {label}
                    </Typography>
                  }
                />
                <div className={styles.barTrack}>
                  <div
                    className={styles.bar}
                    style={{ width: `${(stat.total / maxTotal) * 100}%` }}
                  >
                    {EXIT_CAUSES.filter((cause) => stat.byCause[cause] > 0).map(
                      (cause) => (
                        <span
                          key={cause}
                          className={cn(
                            styles.barPart,
                            idxMap(
                              CAUSE_COLOR_VARIANTS as Record<string, string>,
                              cause,
                            ),
                          )}
                          style={{ flexGrow: stat.byCause[cause] }}
                          title={`${getExitCauseLabel(cause)}: ${
                            stat.byCause[cause]
                          }`}
                        />
                      ),
                    )}
                  </div>
                </div>
                <Typography
                  className={styles.count}
                  variant="body3"
                  weight="medium"
                  mode="compact"
                >
                  {stat.total}
                </Typography>
                {problem ? (
                  <Typography
                    className={cn(
                      styles.problem,
                      idxMap(
                        PROBLEM_TEXT_VARIANTS as Record<string, string>,
                        problem.cause,
                      ),
                    )}
                    variant="body3"
                    mode="compact"
                  >
                    {problem.text}
                  </Typography>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default React.memo(ExitScreens) as any;
